// the pseudo-fullscreen view for Editing a panel
import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Box, Flex, Grid, Heading, Text } from 'core/components';
import $decks from 'app/stores/decks/$decks';
import Page from 'app/components/page/Page';
import NotFound from 'app/views/NotFound';
import { FormComponent } from 'core/form';
import deckTypes from '@kentik/ui-shared/boards/deckTypes';
import { debounce } from 'lodash';
import BoardSubnavTools from 'app/views/boards/BoardSubnavTools';
import PanelTitle from 'app/views/boards/PanelTitle';
import DraftTag from 'app/views/boards/DraftTag';
import UdeToolBar from 'app/views/boards/panels/ude/UdeToolBar';
import BoardSettings from 'app/views/boards/BoardSettings';
import CollapsedPanelStrip from 'app/views/boards/panels/ude/CollapsedPanelStrip';
import PanelEditContext from './PanelEditContext';
import PanelEditSidebar from './PanelEditSidebar';
import UnsavedChangesPrompt from './UnsavedChangesPrompt';
import BoardPanelView from './BoardPanelView';
import { getWidgetFields } from './panels/widget/utils';

// basic fields for a panel
const defaultPanelFields = {
  title: {
    label: 'Title',
    defaultValue: ''
  },
  'config.description': {
    label: 'Description',
    defaultValue: ''
  },
  'config.showTitle': {
    label: 'Show title',
    defaultValue: true
  },
  'config.variableMappings': {
    label: 'Variable Mappings',
    isComplexArray: true,
    defaultValue: []
  },
  'config.variableMappings[].variableName': {
    label: 'Variable Name',
    defaultValue: ''
  },
  'config.variableMappings[].operator': {
    label: 'Operator',
    defaultValue: '='
  },
  'config.variableMappings[].filterField': {
    label: 'Filter Field',
    defaultValue: ''
  },
  'config.variableMappings[].disabled': {
    label: 'Disabled',
    defaultValue: false
  }
};

function getPanelFields(panel) {
  const { type, config } = panel.get();

  if (type === 'widget') {
    return getWidgetFields(config.widgetName);
  }

  return {};
}

@inject('$auth', '$boards')
@withRouter
@observer
class PanelEditView extends Component {
  state = {
    isLoading: true,
    isSettingsOpen: false,
    queryResetSignal: 0,
    rowFiltersPending: false,
    discardSignal: 0,
    leftCollapsed: typeof window !== 'undefined' && window.localStorage?.getItem('ude-left-collapsed') === 'true',
    rightCollapsed: typeof window !== 'undefined' && window.localStorage?.getItem('ude-right-collapsed') === 'true'
  };

  handleRowFiltersAdded = () => {
    this.setState({ rowFiltersPending: true });
  };

  handleToggleLeftCollapsed = () => {
    this.setState((prev) => {
      const next = !prev.leftCollapsed;
      try {
        window.localStorage?.setItem('ude-left-collapsed', String(next));
      } catch (e) {
        /* localStorage unavailable - non-fatal */
      }
      return { leftCollapsed: next };
    });
  };

  handleToggleRightCollapsed = () => {
    this.setState((prev) => {
      const next = !prev.rightCollapsed;
      try {
        window.localStorage?.setItem('ude-right-collapsed', String(next));
      } catch (e) {
        /* localStorage unavailable - non-fatal */
      }
      return { rightCollapsed: next };
    });
  };

  get isUde() {
    const { location } = this.props;
    return location.pathname.startsWith('/v4/ude');
  }

  get basePath() {
    return this.isUde ? '/v4/ude' : '/v4/library';
  }

  /**
   * Match the Deck that the user came from, if any
   */
  get deck() {
    const { location } = this.props;
    const fromPath = location.state?.from?.pathname;

    if (fromPath) {
      return Object.values(deckTypes).find((d) => d.path === fromPath);
    }

    return undefined;
  }

  get parentLinks() {
    const { board, match } = this.props;
    const { deck } = this;

    if (!board || !match.params.panelId) {
      return [];
    }

    const title = board.get('title') || 'Untitled';

    if (board.singlePanelView) {
      return [];
    }
    if (deck) {
      return [{ link: deck.path, label: deck.label }];
    }
    if (board.isNew) {
      return [{ link: `${this.basePath}/boards/new`, label: title }];
    }
    return [{ link: `${this.basePath}/boards/${board.id}`, label: title }];
  }

  autoSave = debounce((values = {}) => {
    const { onSavePanel } = this.props;
    onSavePanel(values, { draft: true });
  }, 300);

  componentWillUnmount() {
    this.autoSave.cancel();
  }

  handleToggleSettingsDrawer = () => {
    const { isSettingsOpen } = this.state;
    this.setState({ isSettingsOpen: !isSettingsOpen });
  };

  handleChangePanel = ({ form, formValues, field }) => {
    // query data should only be saved on run/save
    if (form.valid && !field?.originalName.startsWith('config.data.')) {
      this.autoSave(formValues);
    }
  };

  handleSavePanel = async (values = {}, options = {}) => {
    const { panel, board, history, location, onSavePanel } = this.props;
    const saveOptions = { draft: false, ...options };
    panel.safeSet(values);
    const result = await onSavePanel(values, saveOptions);
    // After an explicit Save, return the user to the dashboard (unless we're
    // in the single-panel view or this is a draft save).
    if (!board.singlePanelView && !saveOptions.draft) {
      history.push({ pathname: `${this.basePath}/boards/${board.id}`, search: location.search });
    }
    return result;
  };

  handleSaveTitle = (title) => {
    const { onSavePanel } = this.props;
    this.form.setValue('title', title);
    return onSavePanel({ title }, { draft: true });
  };

  /**
   * @param {import('@kentik/ui-shared/query/query.types').TimeRange} time
   */
  handleTimeChange = (time) => {
    this.form.setValue('config.data.time.lookback', time.lookback);
    this.form.setValue('config.data.time.start', time.start);
    this.form.setValue('config.data.time.end', time.end);
    this.handleRunQuery();
  };

  handleTimezoneChange = (timezone) => {
    this.form.setValue('config.chart.xAxis.timezone', timezone);
  };

  /**
   * @param {number} updateFrequency
   */
  handleLiveUpdateChange = (updateFrequency) => {
    this.form.setValue('config.data.updateFrequency', updateFrequency);
    this.handleRunQuery();
  };

  handleCancel = async () => {
    const { panel, board, history, location } = this.props;
    const { hasOnlyDraftVersion } = panel;

    this.autoSave.cancel();

    await panel.whenSaveSettled();

    if (panel.isDirty) {
      await panel.discardDraft({ toast: !hasOnlyDraftVersion });
    }

    if (!board.singlePanelView && hasOnlyDraftVersion) {
      history.push({
        pathname: `${this.basePath}/boards/${board.id}`,
        search: location.search,
        state: { ignoreUnsavedChanges: true }
      });
      return;
    }

    this.form?.setModel(panel);
    this.setState((prev) => ({ discardSignal: prev.discardSignal + 1 }));
  };

  handleToggleLoading = () => {
    this.setState((prevState) => ({
      isLoading: !prevState.isLoading
    }));
  };

  handleResetExplorer = () => {
    // Reset query data fields to their defaults
    this.form.fieldStates.forEach((field) => {
      if (
        /^config\.data\.(dimensions|filters|measurement|metrics|reAgg|rollups|sort|window)/.test(field.originalName)
      ) {
        field.setToDefault({ validate: false });
      }
    });

    // Reset chart config fields dependent on data fields
    this.form.setValue('config.chart.yAxis.metric', '');
    this.form.setValue('config.chart.yAxis.aggregation', '');
    this.form.setValue('config.chart.yAxis.multipleAxis', false);

    // Signal child forms (e.g. UDE) to reset their local UI state (category picker)
    this.setState((prev) => ({ queryResetSignal: prev.queryResetSignal + 1 }));

    // Push the cleared form state to the panel model so the dataview resets to empty state
    this.handleRunQuery();
  };

  handleRunQuery = () => {
    const { panel } = this.props;
    const formConfig = this.form.getValues().config;
    panel.applyQueryConfig(formConfig);
    this.setState({ rowFiltersPending: false });

    if (panel.isNew || panel.isDirty) {
      this.autoSave();
    }
  };

  render() {
    const { $boards, board, panel, onSaveBoard, onAddPanel } = this.props;
    const { isSettingsOpen, leftCollapsed, rightCollapsed, queryResetSignal, rowFiltersPending, discardSignal } =
      this.state;

    if (!board || !panel) {
      return (
        <NotFound
          parentLinks={this.parentLinks}
          title="Not Found"
          text="Unable to find this panel"
          hideSubnav={false}
        />
      );
    }

    const drawerContents = isSettingsOpen ? (
      <BoardSettings board={board} onClose={this.handleToggleSettingsDrawer} onSaveBoard={onSaveBoard} />
    ) : null;

    const panelConfig = $boards.getPanelConfig(panel);
    const {
      fields: panelTypeFields,
      form: PanelTypeForm,
      settings: SettingsForm,
      panelTools: PanelTools,
      sidebarWidth = 'min(425px, 38%)'
    } = panelConfig;

    let panelFields = { ...defaultPanelFields, ...getPanelFields(panel) };

    if (panelTypeFields) {
      panelFields = { ...panelFields, ...panelTypeFields };
    }

    const effectiveSidebarWidth = sidebarWidth;

    const panelEditContextValue = { onRunQuery: this.handleRunQuery, onRowFiltersAdded: this.handleRowFiltersAdded };

    return (
      <PanelEditContext.Provider value={panelEditContextValue}>
        <FormComponent
          fields={panelFields}
          model={panel}
          onChange={this.handleChangePanel}
          options={{ name: 'panel-form' }}
        >
          {({ form }) => {
            this.form = form;
            const values = form.getValues();
            const { config } = values;

            const shouldShowSettings =
              SettingsForm &&
              (typeof panelConfig.shouldShowSettings === 'function'
                ? panelConfig.shouldShowSettings(config)
                : panelConfig.shouldShowSettings !== false) &&
              // For UDE panels, only reveal the Chart settings pane once a query has been executed
              (panel.get('type') !== 'ude' || !!panel.queryModel?.hasRun);
            const leftWidth = leftCollapsed ? '52px' : effectiveSidebarWidth;
            let rightWidth = '';
            if (shouldShowSettings) {
              rightWidth = rightCollapsed ? '40px' : '285px';
            }
            const gridTemplateColumns = `${leftWidth} 1fr ${rightWidth}`;

            return (
              <Page
                title={`${form.getValue('title') || panel.title}`}
                titleElement={
                  <Flex alignItems="center" gap="4px">
                    <PanelTitle panel={panel} canEdit onSave={this.handleSaveTitle} />
                    <DraftTag board={board} panel={panel} />
                  </Flex>
                }
                parentLinks={this.parentLinks}
                px={0}
                py={0}
                flexed
                overflow="hidden"
                subnavTools={
                  <BoardSubnavTools
                    form={form}
                    board={board}
                    panel={panel}
                    panelType={panel.get('type')}
                    onSave={this.handleSavePanel}
                    onShowSettings={this.handleToggleSettingsDrawer}
                    onAddPanel={onAddPanel}
                    onCancel={this.handleCancel}
                  />
                }
                drawerProps={{ showDockedInLargeFullScreen: false }}
                drawerContents={drawerContents}
                drawerIsOpen={isSettingsOpen}
                drawerOnClose={this.handleToggleSettingsDrawer}
              >
                <UnsavedChangesPrompt board={board} panel={panel} form={form} />
                <Grid
                  className="ude-layout-grid"
                  gridTemplateColumns={gridTemplateColumns}
                  gridTemplateRows="1fr"
                  gridGap={0}
                  style={{ transition: 'grid-template-columns 0.3s ease' }}
                  gridRowGap={0}
                  width="100%"
                  bg="sectionBackground"
                  height="100%"
                  flex={1}
                  overflow="hidden"
                >
                  <Flex
                    flexDirection="column"
                    height="100%"
                    width="100%"
                    minWidth={leftCollapsed ? 0 : 300}
                    overflow="hidden"
                  >
                    <PanelEditSidebar
                      form={form}
                      panel={panel}
                      board={board}
                      panelConfig={panelConfig}
                      onSave={this.handleSavePanel}
                      onCancel={this.handleCancel}
                      panelTypeFields={panelTypeFields}
                      formComponent={PanelTypeForm}
                      onReset={this.handleResetExplorer}
                      onToggleLoading={this.handleToggleLoading}
                      onRunQuery={this.handleRunQuery}
                      queryResetSignal={queryResetSignal}
                      rowFiltersPending={rowFiltersPending}
                      discardSignal={discardSignal}
                      collapsed={leftCollapsed}
                      onToggleCollapse={this.handleToggleLeftCollapsed}
                    />
                  </Flex>

                  <Flex
                    flexDirection="column"
                    gap="12px"
                    width="100%"
                    height="100%"
                    overflow="hidden"
                    p="12px 12px 12px 1px"
                  >
                    {panel.get('type') === 'ude' && (
                      <UdeToolBar
                        board={board}
                        panel={panel}
                        hasMeasurement={!!form.getValue('config.data.measurement')}
                        onTimeChange={this.handleTimeChange}
                        onTimezoneChange={this.handleTimezoneChange}
                        onLiveUpdateChange={this.handleLiveUpdateChange}
                      />
                    )}
                    {panel.get('type') === 'widget' &&
                      (() => {
                        const widgetName = form.getValue('config.widgetName');
                        const widgetConfig = widgetName ? $decks.widgets[widgetName] : null;
                        if (!widgetConfig) {
                          return null;
                        }
                        return (
                          <Box pb={1}>
                            <Heading level={5} mb="4px">
                              {widgetConfig.category} | {widgetConfig.title}
                            </Heading>
                            <Text color="muted">{widgetConfig.description}</Text>
                          </Box>
                        );
                      })()}
                    <BoardPanelView
                      board={board}
                      panel={panel}
                      form={form}
                      canCustomize={false}
                      customRightElements={PanelTools ? <PanelTools panel={panel} form={form} /> : undefined}
                      onSaveTitle={this.handleSaveTitle}
                    />
                  </Flex>

                  {shouldShowSettings && rightCollapsed && (
                    <Flex flexDirection="column" height="100%" width="100%" overflow="hidden">
                      <CollapsedPanelStrip label="Chart" direction="right" onClick={this.handleToggleRightCollapsed} />
                    </Flex>
                  )}

                  {shouldShowSettings && !rightCollapsed && (
                    <Flex
                      flexDirection="column"
                      height="100%"
                      width="100%"
                      minWidth={285}
                      p="12px 0px 12px 1px"
                      mb={0}
                      overflow="auto"
                      flex={1}
                    >
                      <SettingsForm
                        form={form}
                        panel={panel}
                        board={board}
                        onToggleCollapse={this.handleToggleRightCollapsed}
                      />
                    </Flex>
                  )}
                </Grid>
              </Page>
            );
          }}
        </FormComponent>
      </PanelEditContext.Provider>
    );
  }
}

export default PanelEditView;
